/**
 * Tiny skeleton block used as a placeholder while dynamic values
 * (deposits, totals, addresses…) are still being fetched. Renders an
 * inline `<span>` so it can sit inside any text flow without disturbing
 * the surrounding layout.
 *
 * Two tones to fit our palette:
 *   - "dark"  (default): subtle dark-green tint, used on mint cards
 *   - "light": white-on-dark, used inside the deep-green sidebar /
 *              Projected-at-Maturity dark card
 */
export default function Shimmer({
    width = "4rem",
    height = "0.875rem",
    tone = "dark",
    className = ""
}) {
    const bg = tone === "light" ? "bg-white/15" : "bg-[#1D3E29]/15";
    return (
        <span
            className={`inline-block animate-pulse rounded ${bg} ${className}`}
            style={{ width, height }}
            aria-hidden="true"
        />
    );
}

/**
 * Visually-hidden, polite status node announced to screen readers while a
 * region's data is loading. Pair this with `aria-busy` on the surrounding
 * loading container and render exactly one per region (not per Shimmer span)
 * so assistive tech announces the pending state once instead of repeatedly.
 *
 * The decorative <Shimmer> spans stay `aria-hidden`; this companion supplies
 * the actual announcement. Uses an inline clip-based sr-only style because no
 * `sr-only` Tailwind utility is wired up in this project.
 *
 * @example
 *   <div aria-busy={loading || undefined}>
 *     {loading && <LoadingStatus label="Loading investment summary…" />}
 *     …
 *   </div>
 */
export function LoadingStatus({ label = "Loading…", as: Tag = "span" }) {
    return (
        <Tag
            role="status"
            className="absolute h-px w-px overflow-hidden whitespace-nowrap [clip:rect(0,0,0,0)]"
        >
            {label}
        </Tag>
    );
}
